import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { AppGlobals } from '@app/app.global';
import { SharedService } from '@app/core';
import { EntityService } from '@app/core/services/entity.service';
import { ApexAnnotations, ApexAxisChartSeries, ApexChart, ApexDataLabels, ApexFill, ApexGrid, ApexLegend, ApexMarkers, ApexStroke, ApexTitleSubtitle, ApexTooltip, ApexXAxis, ApexYAxis } from 'ng-apexcharts';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, takeUntil } from 'rxjs/operators';
import { AnalyticsApiService } from '../../analytics';

type ChartOptions = {
  series: ApexAxisChartSeries;
  annotations: ApexAnnotations;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis | ApexYAxis[];
  dataLabels: ApexDataLabels;
  grid: ApexGrid;
  markers: ApexMarkers;
  labels: string[];
  stroke: ApexStroke;
  title: ApexTitleSubtitle;
  legend: ApexLegend;
  colors: string[];
  tooltip: ApexTooltip;
  fill: ApexFill;
};

@Component({
  selector: 'app-analytics-dashboard',
  templateUrl: './analytics-dashboard.component.html',
  styleUrls: ['./analytics-dashboard.component.scss']
})
export class AnalyticsDashboardComponent implements OnInit, OnDestroy {
  //#region properties
  globalMetrics = {
    activeUsers: {
      count: '0',
      label: 'Active Users (DAU)',
      tooltip: 'Unique users with at least one action today',
      rateDescription: '',
      comparison: ''
    },
    cartConversion: {
      rate: '0',
      label: 'Cart Conversion',
      tooltip: 'Searches resulting in a cart add',
      rateDescription: '',
      comparison: ''
    },
    checkoutConversion: {
      rate: '0',
      label: 'Checkout Rate',
      tooltip: 'Cart adds that proceeded to checkout',
      rateDescription: '',
      comparison: ''
    },
    purchaseCompletion: {
      rate: '0',
      label: 'Purchase Completion',
      tooltip: 'Checkouts that converted to orders',
      rateDescription: '',
      comparison: ''
    },
    overallConversion: {
      rate: '0',
      label: 'Overall Conversion',
      tooltip: 'Searches that converted into confirmed orders',
      rateDescription: '',
      comparison: ''
    },
    totalOrderValue: {
      amount: '0',
      label: "Total Order Value",
      tooltip: "Sum of all order values",
      comparison: ''
    }
  };
  // funnelAnalysis = {
  //   all: {
  //     searches: '0',
  //     cartAdds: '0',
  //     checkouts: '0',
  //     orders: '0'
  //   },
  //   hyperpure: {
  //     searches: '0',
  //     cartAdds: '0',
  //     checkouts: '0',
  //     orders: '0'
  //   },
  //   udaan: {
  //     searches: '0',
  //     cartAdds: '0',
  //     checkouts: '0',
  //     orders: '0'
  //   },
  //   dmart: {
  //     searches: '0',
  //     cartAdds: '0',
  //     checkouts: '0',
  //     orders: '0'
  //   },
  //   local: {
  //     searches: '0',
  //     cartAdds: '0',
  //     checkouts: '0',
  //     orders: '0'
  //   }
  // }
  funnelAnalysis: { funnelFor: string, funnelSteps: { key: string, label: string, value: number, overallProgress: number, conv: number, drop: number, highlight: boolean }[] }[
  ] = []
  funnelData: { key: string, label: string, value: number, overallProgress: number, conv: number, drop: number, highlight: boolean }[] = [];
  cartProducts: Array<{ itemName: string; itemId: string; timesAdded: number, totalQty: number; unitPrice: number; searchToAddRatio: number; supplier: any[]; }> = [];
  filteredCartProducts: Array<{ itemName: string; itemId: string; timesAdded: number, totalQty: number; unitPrice: number; searchToAddRatio: number; supplier: any[]; }> = [];
  activeUsersInsights: Array<{ userName: string; searches: number; cartAdds: number; checkouts: number; orders: number; orderAmount: string; conversionRate: string; segment: string }> = [];
  filteredActiveUsersInsights: Array<{ userName: string; searches: number; cartAdds: number; checkouts: number; orders: number; orderAmount: string; conversionRate: string; segment: string }> = [];
  lineSeries: any = [];
  chartOptions: Partial<ChartOptions> = {};
  groupedBarChartOptions: Partial<ChartOptions> = {};
  chartLabels: string[] = [];
  dateRange: Date[] = [];
  startDate = '';
  endDate = '';
  activeTab = 'all';
  keywordsActiveTab = 'top-keywords';
  orderLogsActiveTab = 'hyperpure';
  cartProductInsightActiveTab = 'most-added-products';
  supplierPerformanceIndicatorData: any[] = [];
  selectedEntityId = '';
  selectedCompany = new FormControl();
  selectedEntities = new FormControl([]);
  parentCompanyList: any = [];
  entities = [];
  private companySearch$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();
  private readonly userBehaviorSearch$ = new Subject<string>();
  private readonly keywordsSearch$ = new Subject<string>();
  private readonly orderLogsSearch$ = new Subject<string>();
  private readonly cartProductsSearch$ = new Subject<string>();
  private readonly activeUsersSearch$ = new Subject<string>();
  searchKeywords: Array<{ keyword: string; searchCount: number; users: number; lastUsed: string }> = [];
  filteredKeywords: Array<{ keyword: string; searchCount: number; users: number; lastUsed: string }> = [];
  orderLogs: any[] = [];
  orderLogsSum = 0;
  searchKeywordsInput: any;
  searchOrderLogsInput: any;
  searchCartProductInput: any;
  searchUserBehaviorInput: any;
  searchUserInput: any;
  analyticsFilterForm!: FormGroup;
  duration: Date[] = [];
  today: Date = new Date();
  searchInsightsQueryParams = {
    startDate: '',
    endDate: '',
    tab: 'top-keywords',
    search: '',
    page: 1,
    pageSize: 10
  }
  orderLogsQueryParams = {
    startDate: '',
    endDate: '',
    tab: 'hyperpure',
    search: '',
    page: 1,
    pageSize: 10
  }

  userBehaviorInsightsQueryParams = {
    startDate: '',
    endDate: '',
    page: 1,
    pageSize: 10,
    search: ''
  }
  userBehaviorInsights: any[] = [];
  companies: any[] = [];
  totalTopKeywords = 0;
  topKeywordsPage = 1;
  topKeywordsPageSize = 10;
  funnelAnalysisInsights = '';
  isLoadingKeywords = false;

  totalOrderLogs = 0;
  orderLogsPage = 1;
  orderLogsPageSize = 10;
  isLoadingOrderLogs = false;

  totalUserBehaviorInsights = 0;
  userBehaviorInsightsPage = 1;
  userBehaviorInsightsPageSize = 10;
  isUserBehaviorInsightsLoading = false;
  tempSelectedEntities: any[] = [];

  //#endregion
  constructor(private readonly analyticsApiService: AnalyticsApiService,
    private readonly sharedService: SharedService,
    public appGlobals: AppGlobals,
    private readonly formBuilder: FormBuilder,
    private readonly entityService: EntityService
  ) {
    this.companySearch$.pipe(debounceTime(this.appGlobals.defaultDebounceTime), distinctUntilChanged())
      .subscribe(term => {
        this.getCompanyList(term);
      });
  }

  ngOnInit() {
    this.entityService.companies$.subscribe((response: any) => {
      if (response?.company?.length) {
        this.parentCompanyList = response.company;
      }
    });
    this.entityService.fetchCompanies();
    this.loadEntities();
    this.setDefaultDateRange();
    this.applyDateRange();
    this.createAnalyticsFilterForm();
    this.setupAllSearches();
    this.setupCompanyEntitySelection();
    this.loadSummary();
    this.loadSearchKeywords();
    this.loadChartData();
    this.getOrderLogs();
    this.getUserBehaviorInsights();
  }

  setupCompanyEntitySelection(): void {
    this.selectedCompany.valueChanges.pipe(debounceTime(1000), takeUntil(this.destroy$)).subscribe(selectedCompany => {
      if (selectedCompany) {
        this.getEntitiesForCompany(selectedCompany);
        this.selectedEntities.setValue([]);
      } else {
        this.entities = [];
        this.selectedEntities.setValue([]);
      }
    });
  }

  loadEntities() {
    this.entities = this.entityService.getEntities();
  }

  setupAllSearches(): void {
    // User Behavior Insights search
    this.userBehaviorSearch$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe((value) => {
      this.userBehaviorInsightsQueryParams.search = value;
      this.getUserBehaviorInsights();
    });

    // Keywords search
    this.keywordsSearch$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe((value) => {
      this.searchInsightsQueryParams.search = value;
      this.fetchKeywordsData(this.keywordsActiveTab);
    });

    // Order Logs search
    this.orderLogsSearch$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe((value) => {
      this.orderLogsQueryParams.search = value;
      this.fetchOrderLogs(this.orderLogsActiveTab);
    });

    // Cart Products search (local filtering, shorter debounce)
    this.cartProductsSearch$.pipe(
      debounceTime(150),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe((value) => {
      if (!value) {
        this.filteredCartProducts = [...this.cartProducts];
        return;
      }
      this.filteredCartProducts = this.cartProducts.filter(item => item.itemName.toLowerCase().includes(value));
    });

    // Active Users search (local filtering, shorter debounce)
    this.activeUsersSearch$.pipe(
      debounceTime(150),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe((value) => {
      if (!value) {
        this.filteredActiveUsersInsights = [...this.activeUsersInsights];
        return;
      }
      this.filteredActiveUsersInsights = this.activeUsersInsights.filter(item => item.userName.toLowerCase().includes(value));
    });
  }

  createAnalyticsFilterForm() {
    const [startDate, endDate] = this.getLast30Days();
    this.duration = this.getLast30Days();

    this.analyticsFilterForm = this.formBuilder.group({
      selectedEntity: [null],
      selectedCompany: [null],
      startDate: [startDate],
      endDate: [endDate],
      duration: [new Date(this.startDate).toLocaleDateString('en-US', this.appGlobals.dateFormat) + ' to ' + new Date(this.endDate).toLocaleDateString('en-US', this.appGlobals.dateFormat)],
      company: [null]
    });
  }

  get f() {
    return this.analyticsFilterForm.controls;
  }

  getLast30Days() {
    const now = new Date();
    const endDate = new Date(now.setHours(23, 59, 59, 999));
    const startDate = new Date(now.setDate(now.getDate() - 29));
    startDate.setHours(0, 0, 0, 0);

    return [startDate, endDate];
  }

  setDefaultDateRange() {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    this.duration = [start, end];
  }

  applyDateRange(): void {
    if (this.duration.length === 2) {
      this.startDate = this.formatLocalDate(this.duration[0]);
      this.endDate = this.formatLocalDate(this.duration[1]);
    }
  }

  formatLocalDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  getDuration(predefinedDuration: string) {
    let duration: Date[] = [];
    switch (predefinedDuration) {
      case 'today':
        duration = [
          new Date(),
          new Date()
        ];
        break;
      case 'thisWeek':
        duration = [
          new Date(new Date().setDate(new Date().getDate() - new Date().getDay())),
          new Date()
        ];
        break;
      case 'lastWeek':
        const firstDayOfTheWeek = new Date(new Date().setDate(new Date().getDate() - new Date().getDay()));
        const lastDayOfTheWeek = new Date(new Date().setDate(new Date().getDate() - new Date().getDay() + 6));
        duration = [
          new Date(firstDayOfTheWeek.setDate(firstDayOfTheWeek.getDate() - 7)),
          new Date(lastDayOfTheWeek.setDate(lastDayOfTheWeek.getDate() - 7))
        ];
        break;
      case 'fortnight':
        duration = [
          new Date(new Date().setDate(new Date().getDate() - 15)),
          new Date()
        ];
        break;
      case 'MTD':
        duration = [
          new Date(new Date().setDate(1)),
          new Date()
        ];
        break;
      case 'YTD':
        duration = [
          new Date(new Date().getFullYear(), 0, 1),
          new Date()
        ];
        break;
      case 'custom':
        duration = this.duration;
        break;
      case 'clear':
        duration = this.getLast30Days();
        break;
      case 'reset':
        duration = this.duration;
        break;
    }
    this.duration = duration;
  }

  setDuration(isCancelled?: boolean) {
    if (isCancelled) {
      // delete this.queryParams['startDate'];
      // delete this.queryParams['endDate'];

      if (this.f.duration.value == '') {
        return
      }

      this.f.startDate.setValue('');
      this.f.endDate.setValue('');
      this.f.duration.setValue('');

      return;
    }

    if (this.duration?.length) {
      this.f.startDate.setValue(this.duration[0]);
      this.f.endDate.setValue(this.duration[1]);
      this.f.duration.setValue(
        this.duration[0].toLocaleDateString('en-US', this.appGlobals.dateFormat) + ' to ' +
        this.duration[1].toLocaleDateString('en-US', this.appGlobals.dateFormat)
      );
    } else {
      const [startDate, endDate] = this.getLast30Days();
      this.f.startDate.setValue(startDate);
      this.f.endDate.setValue(endDate);
      this.f.duration.setValue(`${startDate.toLocaleDateString("en-US", this.appGlobals.dateFormat)} to ${endDate.toLocaleDateString("en-US", this.appGlobals.dateFormat)} `);
    }
    this.applyDateRange();
    this.loadSummary();
    this.loadSearchKeywords();
    this.loadChartData();
    this.getOrderLogs();
    // if (this.queryParams['startDate'] == new Date(this.p.startDate.value).toLocaleDateString('en-US') && this.queryParams['endDate'] == new Date(this.p.endDate.value).toLocaleDateString('en-US')) {
    //   return;
    // }
    // this.queryParams['startDate'] = new Date(this.p.startDate.value).toLocaleDateString('en-US');
    // this.queryParams['endDate'] = new Date(this.p.endDate.value).toLocaleDateString('en-US');
  }

  getCompanies() {
    const companies = JSON.parse(localStorage.getItem('entities'));
    if (companies?.length) {
      companies.forEach((company: any) => {
        company.isSelected = false;
        company.disabled = false;
        this.companies.push(company);
      });
    }
  }

  syncCompanySelection() {
    const selectedIds = this.f.company.value || [];

    this.companies.forEach(outlet => {
      outlet.isSelected = Array.isArray(selectedIds) && selectedIds.includes(outlet.id);
    });
    this.companies = [...this.companies];

    if (selectedIds?.length) {
      this.selectedEntityId = selectedIds.join(',');
    } else {
      this.selectedEntityId = '';
    }
  }

  getCompanyName(id: any): string {
    const company = this.companies.find((outlet: any) => outlet.id === id);
    return company?.name || '';
  }

  onEntityChange() {
    this.syncCompanySelection();
    if (this.f.company.value?.length) {
      this.selectedEntityId = this.f.company.value.join(',');
    } else {
      this.selectedEntityId = '';
    }
    this.loadSummary();
    this.loadSearchKeywords();
    this.loadChartData();
  }

  loadSummary() {
    if (this.duration[0] && this.duration[1]) {
      this.analyticsApiService.getAnalyticsDashboardData({ startDate: this.startDate, endDate: this.endDate, }, this.selectedEntityId).subscribe({
        next: (response) => {
          if (response.kpis && response.funnel) {
            this.buildKeyPerformanceIndicators(response.kpis);
          }

          if (response.funnel && response.supplierFunnels) {
            this.buildFunnelAnalysis(response.funnel, response.supplierFunnels);
            if (response.funnel.insights) {
              const insightObject = response.funnel.insights;
              this.funnelAnalysisInsights = `${this.removeUnderScore(insightObject.largestDropOff.step) || ''}- ${insightObject.largestDropOff.lossVolume} (${insightObject.largestDropOff.dropOffPercent} % drop)`;
            }
          }

          if (response.supplierPerformance && response.funnel) {
            this.buildSupplierPerformanceBreakdown(response.supplierPerformance, response.funnel);
          }
        },
        error: (error) => {
          this.sharedService.errorAlert(error);
        },
      })
    }
  }

  loadSearchKeywords(): void {
    this.isLoadingKeywords = true;
    this.searchInsightsQueryParams.startDate = this.startDate;
    this.searchInsightsQueryParams.endDate = this.endDate;
    this.analyticsApiService.fetchSearchInsights(this.searchInsightsQueryParams, this.selectedEntityId).subscribe({
      next: (response) => {
        if (response.data?.length) {
          this.searchKeywords = [];

          if (this.keywordsActiveTab == 'top-keywords') {
            response.data.forEach((keyword: any) => {
              const keywordObject: any = {
                keyword: keyword.keyword,
                searchCount: keyword.searchCount,
                users: keyword.users,
                lastUsed: keyword.lastUsedRelative
              }

              this.searchKeywords.push(keywordObject);
            });
          } else if (this.keywordsActiveTab == 'keyword-conversion') {
            response.data.forEach((keyword: any) => {
              const keywordObject: any = {
                keyword: keyword.keyword,
                searchCount: keyword.searches || 0,
                cartAdds: keyword.cartAdds || 0,
                orders: keyword.orders || 0,
                conversionRate: keyword.conversionRate || 0,
                status: keyword.status
              }

              if (keyword.supplierBreakdown?.length) {
                keywordObject.supplierBreakdown = [];
                keyword.supplierBreakdown.forEach((supplier: any) => {
                  const supplierObject: any = {
                    name: this.capitalize(supplier.supplierName),
                    cartAdds: supplier.cartAdds || 0,
                    quantity: supplier.quantity || 0,
                  }
                  keywordObject.supplierBreakdown.push(supplierObject);
                });
              }

              this.searchKeywords.push(keywordObject);
            })
          } else if (this.keywordsActiveTab == 'failed_keywords') {
            response.data.forEach((keyword: any) => {
              const keywordObject: any = {
                keyword: keyword.keyword,
                searchCount: keyword.searches || 0,
                users: keyword.users,
                cartAdds: keyword.cartAdds || 0,
                orders: keyword.orders || 0,
                lastUsed: keyword.lastUsedRelative
              }
              this.searchKeywords.push(keywordObject);
            })
          }

          if (response.pagination) {
            this.totalTopKeywords = response.pagination.total;
            this.topKeywordsPage = response.pagination.page;
            this.topKeywordsPageSize = response.pagination.pageSize;
          }
        } else {
          this.filteredKeywords = [];
          this.searchKeywords = [];
        }
        this.isLoadingKeywords = false;
      },
      error: (error) => {
        this.sharedService.errorAlert(error);
        this.isLoadingKeywords = false;
      }
    })

  }

  fetchKeywordsData(tab: string) {
    this.searchKeywords = [];
    this.keywordsActiveTab = tab;
    this.searchInsightsQueryParams.tab = tab;
    this.searchInsightsQueryParams.page = 1;
    this.searchInsightsQueryParams.pageSize = 10;
    this.topKeywordsPage = 1;
    this.topKeywordsPageSize = 10;
    this.loadSearchKeywords();
  }

  fetchOrderLogs(tab: string) {
    this.orderLogs = [];
    this.orderLogsActiveTab = tab;
    this.orderLogsQueryParams.tab = tab;
    this.orderLogsQueryParams.page = 1;
    this.orderLogsQueryParams.pageSize = 10;
    this.getOrderLogs();
  }

  onPageChange(page: number) {
    this.searchInsightsQueryParams.page = page;
    this.loadSearchKeywords();
  }

  onOrderLogsPageSizeChange(page: number) {
    this.orderLogsQueryParams.pageSize = page;
    this.fetchOrderLogs(this.orderLogsActiveTab);
  }

  onOrderLogsPageChange(page: number) {
    this.orderLogsQueryParams.page = page;
    this.fetchOrderLogs(this.orderLogsActiveTab);
  }

  onPageSizeChange(pageSize: number) {
    this.searchInsightsQueryParams.pageSize = pageSize;
    this.loadSearchKeywords();
  }

  onUserBehaviorInsightsPageChange(page: number) {
    this.userBehaviorInsightsQueryParams.page = page;
    this.getUserBehaviorInsights();
  }

  onUserBehaviorInsightsPageSizeChange(pageSize: number) {
    this.userBehaviorInsightsQueryParams.pageSize = pageSize;
    this.getUserBehaviorInsights();
  }

  onSearchUserBehavior() {
    const value = (this.searchUserBehaviorInput || '').trim().toLowerCase();
    this.userBehaviorSearch$.next(value);
  }

  loadChartData() {
    this.analyticsApiService.fetchChartData({ startDate: this.startDate, endDate: this.endDate, entityId: this.selectedEntityId }).subscribe({
      next: (response) => {
        if (response?.kpis?.dailyOrderValue?.dailyBreakdown?.length) {
          this.buildLineChart(response?.kpis?.dailyOrderValue?.dailyBreakdown, response?.kpis?.dailyOrderValue?.supporting);
        }
        if (response?.supplierFunnels) {
          this.buildSupplierChart(response?.supplierFunnels);

        }
      },
      error: (error) => {
        this.sharedService.errorAlert(error);
      }
    })
  }

  getOrderLogs() {
    this.orderLogsQueryParams.startDate = this.startDate;
    this.orderLogsQueryParams.endDate = this.endDate;
    this.isLoadingOrderLogs = true;
    this.analyticsApiService.fetchOrderLogs(this.orderLogsQueryParams, this.selectedEntityId).subscribe({
      next: (response) => {
        if (response?.data?.length) {
          this.orderLogs = [];

          response.data.forEach((orderLog: any) => {
            const orderLogObject: any = {
              entityName: orderLog.entityName,
              orderDate: orderLog.orderDate,
              orderAmount: orderLog.orderAmount,
              orderNumber: orderLog.orderNumber,
            }

            this.orderLogs.push(orderLogObject);
          });
          if (this.orderLogs?.length) {
            this.orderLogsSum = this.orderLogs.reduce((sum, log) => sum + (log.orderAmount || 0), 0);
          }
          if (response.pagination) {
            this.totalOrderLogs = response.pagination.total;
            this.orderLogsPage = response.pagination.page;
            this.orderLogsPageSize = response.pagination.pageSize;
          }
        } else {
          this.orderLogs = [];
        }
        this.isLoadingOrderLogs = false;
      },
      error: (error) => {
        this.sharedService.errorAlert(error);
        this.isLoadingOrderLogs = false;
      }
    })
  }

  getUserBehaviorInsights() {
    this.userBehaviorInsightsQueryParams.startDate = this.startDate;
    this.userBehaviorInsightsQueryParams.endDate = this.endDate;
    this.isUserBehaviorInsightsLoading = true;
    this.analyticsApiService.fetchUserBehaviorInsights(this.userBehaviorInsightsQueryParams, this.selectedEntityId).subscribe({
      next: (response) => {
        if (response.data?.length) {
          this.userBehaviorInsights = [];
          response.data.forEach((behavior: any) => {
            const behaviorObject: any = {
              userName: behavior.userName,
              searches: behavior.searches || 0,
              cartAdds: behavior.cartAdds || 0,
              checkouts: behavior.checkouts || 0,
              orders: behavior.orders || 0,
              orderAmount: behavior.orderAmount || 0,
              conversionRate: behavior.conversionRate || 0,
              segment: behavior.segment || ""
            };

            this.userBehaviorInsights.push(behaviorObject);
          });

          if (response.pagination) {
            this.totalUserBehaviorInsights = response.pagination.total;
            this.userBehaviorInsightsPage = response.pagination.page;
            this.userBehaviorInsightsPageSize = response.pagination.pageSize;
          }
        } else {
          this.userBehaviorInsights = [];
        }
        this.isUserBehaviorInsightsLoading = false;
      },
      error: (error) => {
        this.sharedService.errorAlert(error);
        this.isUserBehaviorInsightsLoading = false;
      }
    })
  }

  loadCartProducts(): void {

    // Primary: fetch from batch-level cartProducts summary
    this.analyticsApiService
      .getCartProducts({
        startDate: this.startDate,
        endDate: this.endDate,
      }, this.selectedEntityId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
        })
      )
      .subscribe(
        (data) => {
          const products = data?.products || data || [];
          if (Array.isArray(products) && products.length > 0) {
            this.cartProducts = products.map((p: any) => ({
              itemName: p.itemName || '',
              itemId: p.itemId || '',
              timesAdded: 0, // attention
              totalQty: p.totalQuantity || 0,
              unitPrice: p.unitPrice || 0,
              searchToAddRatio: 0, // attention
              supplier: Array.isArray(p.supplier) ? p.supplier : []
            }));
            this.filteredCartProducts = [...this.cartProducts];
          } else {
            // Fallback: try individual events endpoint
            // this.loadCartProductsFromEvents();
          }
        },
        () => {
          // Fallback: try individual events endpoint
          // this.loadCartProductsFromEvents();
        }
      );
  }

  buildKeyPerformanceIndicators(KPIs: any) {
    if (KPIs.activeUsersDau) {
      this.globalMetrics.activeUsers.count = KPIs.activeUsersDau.current || 0;
      this.globalMetrics.activeUsers.rateDescription = KPIs.activeUsersDau.supporting.description || "";
      this.globalMetrics.activeUsers.comparison = KPIs.activeUsersDau.changePercent || 0;
    }
    if (KPIs.cartConversionRate) {
      this.globalMetrics.cartConversion.rate = KPIs.cartConversionRate.current || 0;
      this.globalMetrics.cartConversion.rateDescription = KPIs.cartConversionRate.supporting.description || "";
      this.globalMetrics.cartConversion.comparison = KPIs.cartConversionRate.changePercent || 0;
    }

    if (KPIs.checkoutConversionRate) {
      this.globalMetrics.checkoutConversion.rate = KPIs.checkoutConversionRate.current || 0;
      this.globalMetrics.checkoutConversion.rateDescription = KPIs.checkoutConversionRate.supporting.description || "";
      this.globalMetrics.checkoutConversion.comparison = KPIs.checkoutConversionRate.changePercent || 0;
    }

    if (KPIs.purchaseCompletionRate) {
      this.globalMetrics.purchaseCompletion.rate = KPIs.purchaseCompletionRate.current || 0;
      this.globalMetrics.purchaseCompletion.rateDescription = KPIs.purchaseCompletionRate.supporting.description || "";
      this.globalMetrics.purchaseCompletion.comparison = KPIs.purchaseCompletionRate.changePercent || 0;
    }

    if (KPIs.overallConversionRate) {
      this.globalMetrics.overallConversion.rate = KPIs.overallConversionRate.current || 0;
      this.globalMetrics.overallConversion.rateDescription = KPIs.overallConversionRate.supporting.description || "";
      this.globalMetrics.overallConversion.comparison = KPIs.overallConversionRate.changePercent || 0;
    }

    if (KPIs.dailyOrderValue) {
      this.globalMetrics.totalOrderValue.amount = KPIs.dailyOrderValue.current || 0;
      this.globalMetrics.totalOrderValue.comparison = KPIs.dailyOrderValue.changePercent || 0;
    }
  }

  buildSupplierPerformanceBreakdown(supplierData: any, funnelData: any) {
    if (supplierData.suppliers) {
      this.supplierPerformanceIndicatorData = [];
      for (const key in supplierData.suppliers) {
        const supplier = supplierData.suppliers[key];
        const funnelDataCartAdds = funnelData.steps.find((s: any) => s.step == 'cart_adds').count || 0;
        const supplierMetrics = {
          name: this.capitalize(key),
          orders: supplier.ordersContribution.current || 0,
          shareOfOrdersTooltip: `${supplier.orders.current} of ${funnelData.summary?.totalOrders} total orders`,
          conversionTooltip: `${supplier.orders.current} orders from ${funnelDataCartAdds} cart adds`,
          checkoutTooltip: `${supplier.checkoutRate.checkout} checkouts from ${funnelDataCartAdds} cart adds`,
          totalAddedTooltip: `Total items added to cart`,
          conversionRate: supplier.conversionRate.current || 0,
          checkoutRate: supplier.checkoutRate.current || 0,
          totalAdded: supplier.cartVolume.current || 0,
          backGround: key == 'hyperpure' ? '#5e47d10a' : key == 'udaan' ? '#1791cf0d' : key == 'dMart' ? '#f59f0a0a' : key == 'local' ? '#39ac770a' : '#adb5bd',
          supplierIndicator: key == 'hyperpure' ? '#5E47D1' : key == 'udaan' ? '#1791CF' : key == 'dMart' ? '#F59F0A' : key == 'local' ? '#39AC77' : '#adb5bd'
        }

        this.supplierPerformanceIndicatorData.push(supplierMetrics);
      }

      if (supplierData.bestPerformer) {
        const index = this.supplierPerformanceIndicatorData.findIndex(s => s.name.toLowerCase() == supplierData.bestPerformer.supplier.toLowerCase());
        if (index > -1) {
          this.supplierPerformanceIndicatorData[index].isBestPerformer = true;
        }
      }

      if (supplierData.worstPerformer) {
        const index = this.supplierPerformanceIndicatorData.findIndex(s => s.name.toLowerCase() == supplierData.worstPerformer.supplier.toLowerCase());
        if (index > -1) {
          this.supplierPerformanceIndicatorData[index].isWorstPerformer = true;
        }
      }
    }
    // supplierData.forEach(supplier => {
    //   if (supplier._id !== 'all') {
    //     const supplierMetrics = {
    //       name: this.capitalize(supplier._id),
    //       orders: supplier.proceedToBuySuccess || 0,
    //       ordersTooltip: ``,
    //       conversionRate: Number((supplier.proceedToBuySuccess || 0) / (supplier.cartAdds || 0)).toFixed(2),
    //       // conversionRateTooltip: `${supplier.proceedToBuySuccess} orders from ${}`
    //       checkoutRate: Number((supplier.checkouts || 0) / (supplier.cartAdds || 0)).toFixed(2),
    //       totalAdded: supplier.cartAdds || 0,
    //     }
    //     this.supplierPerformanceIndicatorData.push(supplierMetrics);
    //   }
    // })
  }

  buildFunnelAnalysis(allFunnelData: any, supplierFunnels: any) {
    const funnelData = [allFunnelData, ...Object.values(supplierFunnels)];
    this.funnelAnalysis = [];
    funnelData.forEach((funnel) => {
      const funnelObject: any = {
        funnelFor: funnel.supplierFilter.toLowerCase(),

      }
      const funnelSteps: any = [];

      if (funnel.steps?.length) {
        funnel.steps.forEach((step: any) => {
          const stepObject: any = {
            key: step.step,
            label: step.step,
            value: step.count || 0,
            overallProgress: step.count > 0 ? 100 : 0,
            health: step.health
          }
          if (step.step == 'searches') {
            stepObject.value = this.funnelAnalysis[0]?.funnelSteps[0]?.value || step.count || 0;
            stepObject.conv = stepObject.value > 0 ? 100 : 0;
          }
          if (step.step != 'searches') {
            stepObject.conv = step.conversionPercent;
            stepObject.drop = step.dropOffPercent;

            if (funnelObject.funnelFor === 'all' && step.supplierBreakdown) {

              const total = step.count || 1;

              const colorsMap: any = {
                hyperpure: '#6f42c1',
                udaan: '#0dcaf0',
                dmart: '#f59f00',
                local: '#2f9e44'
              };

              stepObject.supplierProgress = Object.keys(step.supplierBreakdown)
                .filter(key => key !== 'all') // ignore "all"
                .map(key => {
                  const count = step.supplierBreakdown[key]?.count || 0;

                  return {
                    key,
                    percent: (count / total) * 100,
                    color: colorsMap[key] || '#adb5bd'
                  };
                })
                .sort((a, b) => b.percent - a.percent); // optional: biggest first
            }
          }

          funnelSteps.push(stepObject);
        });
      }
      funnelObject['funnelSteps'] = funnelSteps;
      this.funnelAnalysis.push(funnelObject);
    });
    this.onChangeTab(this.activeTab);
  }

  buildLineChart(dailyTrend: any[], supportingData: any) {

    // Prepare series
    this.lineSeries = [
      {
        name: 'Searches',
        yAxisIndex: 0,
        data: dailyTrend.map(d => ({
          x: new Date(d._id).getTime(),
          y: d.searches || 0
        }))
      },
      {
        name: 'Orders',
        yAxisIndex: 0,
        data: dailyTrend.map(d => ({
          x: new Date(d._id).getTime(),
          y: d.orderCount || 0
        }))
      },
      {
        name: 'Conversion %',
        yAxisIndex: 1,
        data: dailyTrend.map(d => ({
          x: new Date(d._id).getTime(),
          y: d.conversion
        }))
      }
    ];

    this.chartOptions = {
      series: this.lineSeries,
      title: {
        text: `Trends - Last ${supportingData.daysCount} days`,
        align: 'left',
        margin: 10,
        offsetX: 0,
        offsetY: 0,
        floating: false,
        style: {
          fontSize: '18px',
          fontWeight: 600,
          color: '#212529',
          fontFamily: 'inherit'
        }
      },
      chart: {
        height: 350,
        width: '100%',
        type: 'line',
        toolbar: {
          show: false
        },
        sparkline: {
          enabled: false
        }
      },

      colors: ['#6c757d', '#2f9e44', '#5f3dc4'],

      stroke: {
        curve: "smooth",
        width: [3, 3, 2],
        dashArray: [0, 0, 5] // dotted conversion line
      },

      dataLabels: {
        enabled: false
      },

      markers: {
        size: 4,
        hover: {
          sizeOffset: 4
        }
      },

      grid: {
        borderColor: '#e9ecef',
        strokeDashArray: 4,
        padding: {
          right: 20,
          left: 10
        }
      },

      xaxis: {
        type: "datetime",
        labels: {
          rotate: -15,
          formatter: (val: any, timestamp: number) => {
            const date = new Date(timestamp);
            return date.toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'short'
            });
          }
        }
      },
      yaxis: [
        {
          // title: {
          //   text: "Searches / Orders"
          // },
          labels: {
            formatter: (val: number) => Math.round(val).toString()
          }
        },
        {
          opposite: true,
          // title: {
          //   text: "Conversion %"
          // },
          labels: {
            formatter: (val: number) => val + '%'
          }
        }
      ],

      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'center',
        markers: {
          width: 10,
          height: 10,
          radius: 12,
          fillColors: ['#6c757d', '#2f9e44', '#5f3dc4'],
        }
        // itemMargin: {
        //   horizontal: 10
        // }
      },

      tooltip: {
        shared: true,
        intersect: false,
        x: {
          format: 'dd MMM'
        },
        y: {
          formatter: (val: number, opts: any) => {
            if (opts.seriesIndex === 2) {
              return val + '%'; // conversion
            }
            return val.toString();
          }
        }
      }
    };
  }

  buildSupplierChart(data: any) {

    const categories: string[] = [];
    const cartAdds: number[] = [];
    const checkouts: number[] = [];
    const orders: number[] = [];

    for (const key in data) {

      const supplier = data[key];
      const steps = supplier.steps || [];

      categories.push(this.capitalize(key));

      const cart = steps.find((s: any) => s.step === 'cart_adds')?.count || 0;
      const checkout = steps.find((s: any) => s.step === 'checkout')?.count || 0;
      const order = steps.find((s: any) => s.step === 'orders')?.count || 0;

      cartAdds.push(cart);
      checkouts.push(checkout);
      orders.push(order);
    }

    this.groupedBarChartOptions = {
      series: [
        {
          name: 'Cart Adds',
          data: cartAdds
        },
        {
          name: 'Checkouts',
          data: checkouts
        },
        {
          name: 'Orders',
          data: orders
        }
      ],
      title: {
        text: `Supplier Comparison`,
        align: 'left',
        style: {
          fontSize: '18px',
          fontWeight: 600,
          color: '#212529'
        }
      },
      chart: {
        type: 'bar',
        height: 350,
        toolbar: { show: false }
      },
      // plotOptions: {
      //   bar: {
      //     horizontal: false,
      //     columnWidth: '50%',
      //     borderRadius: 4
      //   }
      // },

      dataLabels: {
        enabled: false
      },

      xaxis: {
        categories: categories
      },

      colors: [
        '#94a3b8', // Cart Adds
        '#3b82f6', // Checkouts
        '#22c55e'  // Orders
      ],

      legend: {
        position: 'bottom',
        horizontalAlign: 'center'
      },

      grid: {
        borderColor: '#e9ecef',
        strokeDashArray: 4
      },

      tooltip: {
        shared: true,
        intersect: false
      }
    };
  }

  buildUserBehavioursInsights(activeUsers: any[]) {
    activeUsers.forEach(user => {
      this.activeUsersInsights.push({
        userName: user.userName || '',
        searches: user.searchCount || 0,
        cartAdds: user.cartAddCount || 0,
        checkouts: user.checkoutCount || 0,
        orders: user.proceedToBuySuccessCount || 0,
        orderAmount: '-',
        conversionRate: '-',
        segment: '-'

      });
    });
    this.filteredActiveUsersInsights = [...this.activeUsersInsights];
  }


  // helper
  capitalize(text: string) {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  onChangeTab(tab: string) {
    this.activeTab = tab;
    this.funnelData = this.funnelAnalysis.find(f => f.funnelFor == tab)?.funnelSteps || [];
  }

  onSearchKeywords() {
    const value = (this.searchKeywordsInput || '').trim().toLowerCase();
    this.keywordsSearch$.next(value);
  }

  onSearchOrderLogs() {
    const value = (this.searchOrderLogsInput || '').trim().toLowerCase();
    this.orderLogsSearch$.next(value);
  }

  onSearchCartProduct() {
    const value = (this.searchCartProductInput || '').trim().toLowerCase();
    this.cartProductsSearch$.next(value);
  }

  onSearchUser() {
    const value = (this.searchUserInput || '').trim().toLowerCase();
    this.activeUsersSearch$.next(value);
  }

  onDurationPickerOpen() {
    if (this.f.startDate.value && this.f.endDate.value) {
      this.duration = [this.f.startDate.value, this.f.endDate.value];
    } else {
      this.duration = this.getLast30Days();
    }
    // set flag for mobile isuue
    // this.hideMobilePicker = false;
  }

  durationChange(duration: Date[]) {
    this.duration = duration;
  }

  exportSearchKeywords() {
    if (!this.searchKeywords || this.searchKeywords.length === 0) {
      this.sharedService.errorAlert('No data to export');
      return;
    }

    let csvContent = '';
    let filename = 'search-keywords.csv';
    const headers: string[] = [];
    const rows: any[] = [];

    // Prepare headers and data based on active tab
    if (this.keywordsActiveTab === 'top-keywords') {
      headers.push('Rank', 'Keyword', 'Search Count', 'Users', 'Last Used');
      this.searchKeywords.forEach((item: any, index) => {
        rows.push([
          (index + 1).toString(),
          item.keyword,
          item.searchCount.toString(),
          item.users.toString(),
          item.lastUsed || '-'
        ]);
      });
      filename = 'top-keywords.csv';
    } else if (this.keywordsActiveTab === 'keyword-conversion') {
      headers.push('Rank', 'Keyword', 'Searches', 'Cart Adds', 'Orders', 'Conv. Rate', 'Status');
      this.searchKeywords.forEach((item: any, index) => {
        rows.push([
          (index + 1).toString(),
          item.keyword,
          item.searchCount.toString(),
          item.cartAdds?.toString() || '0',
          item.orders?.toString() || '0',
          item.conversionRate?.toString() || '-',
          item.status || '-'
        ]);
      });
      filename = 'keyword-conversion.csv';
    } else if (this.keywordsActiveTab === 'failed_keywords') {
      headers.push('Rank', 'Keyword', 'Searches', 'Users', 'Cart Adds', 'Orders', 'Last Used');
      this.searchKeywords.forEach((item: any, index) => {
        rows.push([
          (index + 1).toString(),
          item.keyword,
          item.searchCount.toString(),
          item.users?.toString() || '0',
          item.cartAdds?.toString() || '0',
          item.orders?.toString() || '0',
          item.lastUsed || '-'
        ]);
      });
      filename = 'failed-keywords.csv';
    }

    // Build CSV content with proper formatting
    csvContent = headers.map(h => `"${h}"`).join(',') + '\n';
    rows.forEach(row => {
      csvContent += row.map((cell: string) => `"${cell.toString().replace(/"/g, '""')}"`).join(',') + '\n';
    });

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  exportOrderLogs() {
    if (!this.orderLogs || this.orderLogs.length === 0) {
      this.sharedService.errorAlert('No data to export');
      return;
    }

    const filename = 'order-logs.csv';
    const headers = ['Entity Name', 'Order Date', 'Order Amount', 'Order Number'];
    const rows = this.orderLogs.map(orderLog => [
      orderLog.entityName || '',
      orderLog.orderDate ? new Date(orderLog.orderDate).toLocaleDateString('en-US') : '',
      orderLog.orderAmount != null ? orderLog.orderAmount.toString() : '',
      orderLog.orderNumber ? orderLog.orderNumber.toString() : ''
    ]);

    let csvContent = headers.map(h => `"${h}"`).join(',') + '\n';
    rows.forEach(row => {
      csvContent += row.map(cell => `"${cell?.toString().replace(/"/g, '""')}"`).join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  exportUserBehaviorInsights() {
    if (!this.userBehaviorInsights || this.userBehaviorInsights.length === 0) {
      this.sharedService.errorAlert('No data to export');
      return;
    }

    const filename = 'user-behavior-insights.csv';
    const headers = ['User Name', 'Searches', 'Cart Adds', 'Checkouts', 'Orders', 'Order Amount', 'Conversion Rate', 'Segment'];
    const rows = this.userBehaviorInsights.map(user => [
      user.userName || '',
      user.searches?.toString() || '0',
      user.cartAdds?.toString() || '0',
      user.checkouts?.toString() || '0',
      user.orders?.toString() || '0',
      user.orderAmount?.toString() || '0',
      user.conversionRate?.toString() || '0',
      user.segment || ''
    ]);

    let csvContent = headers.map(h => `"${h}"`).join(',') + '\n';
    rows.forEach(row => {
      csvContent += row.map(cell => `"${cell?.toString().replace(/"/g, '""')}"`).join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
 * This method will check if all entities of a given group are checked
 */
  isGroupChecked(group: any): boolean {
    const findGroupEntities = this.companies.filter((entity: any) => entity.group === group);
    const selectedGroupEntities = findGroupEntities?.filter((entity: any) => entity.isSelected).length;
    return selectedGroupEntities == findGroupEntities?.length;
  }

  /**
  * This method will check if some entities of a given group are checked
  */
  isGroupIndeterminate(group: any): boolean {
    const findGroupEntities = this.companies.filter((entity: any) => entity.group === group);
    const selectedGroupEntities = findGroupEntities?.filter((entity: any) => entity.isSelected).length;
    return selectedGroupEntities > 0 && selectedGroupEntities < findGroupEntities?.length;
  }

  onGroupSelectionChange(event: any, group: any) {
    const checked = event?.checked === true || event?.checked === 'checked';
    const groupEntities = this.companies.filter((entity: any) => entity.group === group);
    const currentSelected = Array.isArray(this.f.company.value) ? [...this.f.company.value] : [];

    if (checked) {
      const groupIds = groupEntities.map((entity: any) => entity.id);
      const merged = Array.from(new Set([...currentSelected, ...groupIds]));
      this.f.company.setValue(merged);
    } else {
      const deselectedIds = groupEntities.map((entity: any) => entity.id);
      const remaining = currentSelected.filter((id: any) => !deselectedIds.includes(id));
      this.f.company.setValue(remaining);
    }

    this.syncCompanySelection();
  }

  toggleRow(item: any) {
    item.open = !item.open;
  }

  onCompanySearch($event: string) {
    this.companySearch$.next($event);
  }

  getCompanyList(searchTerm?: any) {
    const queryParams = searchTerm ? { name: searchTerm.term } : {};
    this.entityService.setCompanies(queryParams).subscribe({
      next: (response: any) => {
        if (response.company?.length) {
          this.parentCompanyList = response.company;
        } else {
          this.parentCompanyList = [];
        }
      },
      error: (error) => {
        console.error('Error fetching companies', error);
        this.parentCompanyList = [];
      }
    });
  }

  getEntitiesForCompany(companyId: string) {
    // Get all entities and filter by company
    const allEntities = this.entityService.getEntities();
    const filteredEntities = allEntities.filter(entity => entity.parentId === companyId).map(entity => ({
      ...entity,
      group: entity.group || 'Other'
    }));
    // Add 'All' option at the beginning
    this.entities = [
      { id: '0', name: 'All', group: '', parentId: companyId, isSelected: false },
      ...filteredEntities
    ];
  }

  onEntityChangeAdmin() {
    // const selectedEntityIds = this.selectedEntities.value || [];
    if (this.selectedEntities.value?.findIndex((entity: any) => entity.id === '0') == 0 && this.selectedEntities.value.length && this.selectedEntities.value[this.selectedEntities.value.length - 1].id === '0') {
      if (this.tempSelectedEntities?.length > this.selectedEntities.value.length) {
        this.selectedEntities.setValue([]);
        this.selectedEntityId = '';
      } else {
        this.selectedEntities.setValue(this.entities);
        const selectedEntityIds = this.entities
          .filter((entity: any) => entity.id !== '0')
          .map((entity: any) => entity.id);

        this.selectedEntityId = selectedEntityIds.join(',');
      }
    } else if (this.selectedEntities.value?.findIndex((entity: any) => entity.id === '0') === -1) {
      if (this.tempSelectedEntities.findIndex((entity: any) => entity.id === '0') > -1) {
        this.selectedEntities.setValue([]);
        this.selectedEntityId = '';
      } else if (this.selectedEntities.value.length === this.entities.length - 1 && this.selectedEntities.value.findIndex((entity: any) => entity.id === '0') === -1) {
        const selectedEntities: any = this.selectedEntities.value;
        selectedEntities.unshift({ id: '0', name: 'All' });
        this.selectedEntities.setValue(selectedEntities);
        this.selectedEntityId = this.entities.filter((entity: any) => entity.id !== '0').map((entity: any) => entity.id).join(',');
      } else if (this.selectedEntities.value.length && !this.tempSelectedEntities.length && this.selectedEntities.value.findIndex((entity: any) => entity.id === '0') === -1) {
        const selectedEntities: any = this.selectedEntities.value;
        const selectedEntityIds = selectedEntities.map((entity: any) => entity.id).join(',');
        this.selectedEntityId = selectedEntityIds;
      } else {
        const selectedEntities: any = this.selectedEntities.value;
        const selectedEntityIds = selectedEntities.filter((entity: any) => entity.id !== '0').map((entity: any) => entity.id).join(',');
        this.selectedEntityId = selectedEntityIds;
      }
    } else {
      const selectedEntities: any = this.selectedEntities.value;
      const entitiesWithoutAll: any = this.selectedEntities.value?.filter((entity: any) => entity.id !== '0');
      this.selectedEntities.setValue(entitiesWithoutAll);
      const selectedEntityIds = selectedEntities.filter((entity: any) => entity.id !== '0').map((entity: any) => entity.id).join(',');
      this.selectedEntityId = selectedEntityIds;
    }
    this.tempSelectedEntities = [...this.selectedEntities.value];
    // this.selectedEntityId = selectedEntityIds.join(',');
    // Reload data

  }

  reloadData() {
    this.loadSummary();
    this.loadSearchKeywords();
    this.loadChartData();
    this.getOrderLogs();
    this.getUserBehaviorInsights();
  }

  /**
   * This method will replace underscore with space
   * @param label 
   * @returns 
   */
  removeUnderScore(label: string) {
    return label.replace(/_/g, ' ');
  }

  onCompanyDropdownClose() {
    // Handle close if needed
  }

  onEntitySelectionCloseAdmin() {
    // Handle close if needed
  }

  onSelectionOpenAdmin() {
    // Handle open if needed
  }

  isAllSelected(items: any[]): boolean {
    return items?.some(item => item.id === '0');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
